import * as Yup from "yup";

/** Field shorthands that also attach "type" and custom meta (e.g., aliases) */
export const yString = (meta = {}) =>
  Yup.string()
    .trim()
    .meta({ type: "string", ...meta });
export const yNumber = (meta = {}) =>
  Yup.number().meta({ type: "number", ...meta });
export const yBoolean = (meta = {}) =>
  Yup.boolean().meta({ type: "boolean", ...meta });
export const yJSON = (meta = {}) => Yup.mixed().meta({ type: "json", ...meta });
export const yDate = (meta = {}) => Yup.date().meta({ type: "date", ...meta });

/** Array with inner type carried via meta for our normalizer */
export const yArray = (inner, meta = {}) =>
  Yup.array()
    .of(inner)
    .meta({
      type: `array<${inner?.specType || inner?.describe?.().type || inner?.meta?.().type || "mixed"}>`,
      ...meta,
    });

/** Weak cache so we don't rebuild the spec every call */
const SPEC_CACHE = new WeakMap();

/** Map Yup's describe().type + meta → our spec type string */
function resolveFieldType(described) {
  const metaType = described?.meta?.type;
  if (metaType) return metaType; // we set this via helpers above

  // Fallbacks based on Yup type
  if (described?.type === "string") return "string";
  if (described?.type === "number") return "number";
  if (described?.type === "boolean") return "boolean";
  if (described?.type === "date") return "date";
  if (described?.type === "array") {
    const inner = described?.innerType;
    const innerType = resolveFieldType(inner);
    return `array<${innerType || "mixed"}>`;
  }
  return "json"; // safest generic
}

/** Is this field unconditionally required? (conditionals stay in Yup) */
function isUnconditionalRequired(described) {
  const tests = described?.tests || [];
  return tests.some((t) => t.name === "required" && !t.params?.ref);
}

/**
 * extractSpecFromYup
 * Turn a Yup object schema (with meta) into the "spec" your normalizer expects.
 * - Pulls aliases/type/default/required from field meta/describe
 * - Pulls root-level filters/files from schema.meta()
 */
export function extractSpecFromYup(objectSchema) {
  if (SPEC_CACHE.has(objectSchema)) return SPEC_CACHE.get(objectSchema);

  const desc = objectSchema.describe();
  if (desc.type !== "object") throw new Error("Schema must be a Yup.object()");

  const fields = {};
  for (const [name, def] of Object.entries(desc.fields || {})) {
    const type = resolveFieldType(def);
    const required = isUnconditionalRequired(def);
    const meta = def.meta || {};
    const aliases =
      Array.isArray(meta.aliases) && meta.aliases.length
        ? meta.aliases
        : [name];

    // Yup puts default under .default only if set; else undefined
    const hasDefault = Object.prototype.hasOwnProperty.call(def, "default");
    const defaultValue = hasDefault ? def.default : undefined;

    fields[name] = {
      aliases,
      type,
      required,
      ...(hasDefault ? { default: defaultValue } : {}),
    };
  }

  // Optional: root-level meta for filters/files (used by your normalizer)
  const rootMeta = desc.meta || {};
  const spec = {
    fields,
    ...(rootMeta.filters ? { filters: rootMeta.filters } : {}),
    ...(rootMeta.files ? { files: rootMeta.files } : {}),
  };

  SPEC_CACHE.set(objectSchema, spec);
  return spec;
}

export function yFlag({ aliases = [] } = {}) {
  return Yup.number()
    .transform((_val, orig) => {
      const v = orig;
      if (v === true) return 1;
      if (v === false) return 0;
      const s = String(v ?? "")
        .trim()
        .toLowerCase();
      if (s === "1" || s === "true" || s === "yes" || s === "y" || s === "on")
        return 1;
      if (s === "0" || s === "false" || s === "no" || s === "n" || s === "off")
        return 0;
      return 0; // safe default
    })
    .oneOf([0, 1], "Must be 0 or 1")
    .default(0)
    .meta({ aliases, type: "number" }); // spec/extractor sees it as numeric flag
}
